import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { Shield, Users, ChevronRight, ArrowLeft, Bell, CalendarRange, BookOpenCheck, MessageCircleQuestion, Sun } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/sourceMeta";
import { fmtDue, subjectStyle } from "@/lib/subjects";
import { AnnouncementsAdmin, UsageTrends } from "@/components/Announcements";
import { ContactInbox } from "@/pages/Contact";
import { startActAs } from "@/components/ActAs";
import { UserCog } from "lucide-react";
import { SafetyPanel, PremiumToggle } from "@/components/AdminSafety";

const Stat = ({ icon: I, n, label }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700"><I className="w-3.5 h-3.5 text-gold-300" />{n} {label}</span>
);

const UserRow = ({ u, active, onPick }) => (
  <button data-testid={`admin-user-${u.user_id}`} onClick={() => onPick(u.user_id)}
    className={`w-full text-left rounded-2xl border p-4 transition-colors ${active ? "border-gold-400 bg-gold-50" : "border-slate-200 bg-surface hover:bg-slate-50"}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 truncate">{u.name || "No name"}{u.is_admin && <span className="ml-2 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white align-middle">ADMIN</span>}{u.disabled && <span className="ml-2 rounded-full bg-slate-300 px-2 py-0.5 text-[10px] font-bold text-slate-900 align-middle">DISABLED</span>}</p>
        <p className="text-sm text-slate-500 truncate">{u.email}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">
      <Stat icon={BookOpenCheck} n={u.homework_open ?? 0} label="homework" />
      <Stat icon={CalendarRange} n={u.tests_upcoming ?? 0} label="tests" />
      <Stat icon={Sun} n={u.digests ?? 0} label="briefs" />
      <Stat icon={Bell} n={u.push_devices ?? 0} label="devices" />
    </div>
    <p className="mt-2 font-mono text-[11px] text-slate-500">Timetable: {(u.timetable_weeks ?? []).join(" + ") || "none"} · Term dates: {u.has_calendar ? "yes" : "no"} · Last seen: {u.last_seen ? fmtTime(u.last_seen) : "—"}</p>
  </button>
);

const Section = ({ title, id, children }) => (
  <section data-testid={id} className="rounded-2xl bg-surface border border-slate-200 p-5">
    <h3 className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-gold-300">{title}</h3>
    <div className="mt-3">{children}</div>
  </section>
);
const Empty = ({ children }) => <p className="text-sm text-slate-500">{children}</p>;

const Timetables = ({ list }) => (list?.length ? list.map((t) => (
  <div key={t.week} className="mb-4 last:mb-0">
    <p className="text-sm font-semibold text-slate-900">Week {t.week}</p>
    <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
      {Object.entries(t.days ?? {}).map(([day, ls]) => (
        <div key={day} className="rounded-xl bg-slate-50 border border-slate-200 p-2.5">
          <p className="text-xs font-bold text-slate-700">{day}</p>
          <ul className="mt-1 space-y-0.5">{(ls ?? []).map((l, i) => <li key={i} className="text-xs text-slate-600 truncate"><span className="font-mono text-slate-500">{l.start}</span> {l.subject}</li>)}</ul>
        </div>
      ))}
    </div>
  </div>
)) : <Empty>No timetable uploaded.</Empty>);

const HomeworkTable = ({ list }) => (list?.length ? (
  <ul className="divide-y divide-slate-200">
    {list.map((h) => (
      <li key={h.hw_id} className="py-2.5 flex flex-wrap items-center gap-2 text-sm">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${h.kind === "test" ? "bg-navy-600 text-white" : "bg-slate-200 text-slate-700"}`}>{h.kind === "test" ? "TEST" : "HW"}</span>
        <span className={`flex-1 min-w-0 ${h.done ? "line-through text-slate-500" : "text-slate-900"}`}>{h.title}</span>
        {h.subject && <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${subjectStyle(h.subject).chip}`}>{h.subject}</span>}
        <span className="font-mono text-xs text-slate-500">{fmtDue(h.due)}</span>
        {Array.isArray(h.plan) && h.plan.length > 0 && <span className="text-xs text-gold-300">plan {h.plan.filter((s) => s.done).length}/{h.plan.length}</span>}
      </li>
    ))}
  </ul>
) : <Empty>No homework or tests.</Empty>);

const AdminRole = ({ u, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  if (u.root_admin) return <p data-testid="admin-role-permanent" className="mt-4 text-xs text-slate-500">Permanent admin. These rights can't be removed here.</p>;
  const grant = !u.is_admin;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/admin`, { grant, code });
      toast.success(grant ? `${u.email} is now an admin` : `Admin rights removed from ${u.email}`);
      setOpen(false); setCode(""); onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  if (!open) return (
    <button data-testid="admin-role-toggle" onClick={() => setOpen(true)} className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:border-gold-400">
      <Shield className="w-4 h-4" />{grant ? "Make admin" : "Remove admin"}
    </button>
  );
  return (
    <form data-testid="admin-role-form" onSubmit={submit} className="mt-4 flex flex-wrap items-center gap-2 rounded-xl border border-gold-400/60 p-3">
      <label htmlFor="admin-code" className="w-full text-sm font-semibold text-slate-800">{grant ? `Enter the admin code to make ${u.name || u.email} an admin` : "Enter the admin code to remove admin rights"}</label>
      <input id="admin-code" data-testid="admin-code-input" type="password" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} required
        className="h-10 w-40 rounded-xl border border-slate-200 bg-surface px-3 text-sm text-slate-900 outline-none focus:border-gold-400" />
      <button data-testid="admin-code-submit" disabled={busy || !code} className="h-10 rounded-xl bg-gold-400 px-4 text-sm font-bold text-black disabled:opacity-50">{grant ? "Make admin" : "Remove admin"}</button>
      <button type="button" data-testid="admin-code-cancel" onClick={() => { setOpen(false); setCode(""); }} className="h-10 px-3 text-sm text-slate-600">Cancel</button>
    </form>
  );
};

const AdminActions = ({ u, onChanged }) => {
  if (u.is_admin) return <AdminRole u={u} onChanged={onChanged} />;
  const toggle = async () => {
    try { await api.post(`/admin/users/${u.user_id}/disable`, { disabled: !u.disabled }); toast.success(u.disabled ? "Account enabled" : "Account disabled"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${u.email} and all their data? This can't be undone.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("Account deleted"); onChanged(true); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
    <PremiumToggle u={u} />
    <AdminRole u={u} onChanged={onChanged} />
    <div className="mt-4 flex flex-wrap gap-2">
      <button data-testid="admin-manage-button" onClick={() => startActAs(u)} className="inline-flex items-center gap-2 rounded-xl bg-gold-400 px-4 py-2 text-sm font-bold text-black hover:bg-gold-300"><UserCog className="w-4 h-4" />Manage this account</button>
      <button data-testid="admin-disable-button" onClick={toggle} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-100">{u.disabled ? "Enable account" : "Disable account"}</button>
      <button data-testid="admin-delete-button" onClick={del} className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700">Delete account</button>
    </div>
    </>
  );
};

const Detail = ({ uid, onBack, onChanged }) => {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/users/${uid}`).then((r) => setD(r.data ?? null)).catch(() => setD(false));
  useEffect(() => { setD(null); load(); }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (d === false) return <Empty>Couldn't load this user.</Empty>;
  if (!d) return <div className="h-40 rounded-2xl bg-surface animate-pulse" />;
  const s = d.settings ?? {};
  return (
    <div data-testid="admin-detail" className="space-y-4">
      <button data-testid="admin-back" onClick={onBack} className="lg:hidden inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600"><ArrowLeft className="w-4 h-4" />All users</button>
      <Section title="Profile" id="admin-profile">
        <p className="text-xl font-semibold text-slate-900">{d.user?.name}</p>
        <p className="text-sm text-slate-600">{d.user?.email}</p>
        <p className="mt-2 font-mono text-xs text-slate-500">Joined {fmtTime(d.user?.created_at)} · Last seen {d.user?.last_seen ? fmtTime(d.user.last_seen) : "—"} · Setup {d.user?.onboarded === false ? "not finished" : "done"}{d.user?.disabled ? " · DISABLED" : ""}</p>
        <p className="mt-1 text-xs text-slate-500">Email brief: {s.email ? (s.email.enabled ? `on → ${s.email.email_to}` : "off") : "default"} · Kit reminders: {s.kit ? (s.kit.enabled === false ? "off" : `${(s.kit.rules ?? []).length} rules`) : "default"} · Phone devices: {(d.push_devices ?? []).map((p) => p.device).join(", ") || "none"}</p>
        <AdminActions u={d.user ?? {}} onChanged={(gone) => { onChanged(); if (gone) onBack(); else load(); }} />
      </Section>
      <Section title="Homework & tests" id="admin-homework"><HomeworkTable list={d.homework} /></Section>
      <Section title="Timetable" id="admin-timetable"><Timetables list={d.timetables} /></Section>
      <Section title="Term dates" id="admin-calendar">
        {d.calendar ? <p className="text-sm text-slate-700">{d.calendar.year} · {d.calendar.weeks} weeks labelled · {(d.calendar.closed ?? []).length} breaks</p> : <Empty>No term dates uploaded.</Empty>}
      </Section>
      <Section title="Recent briefs" id="admin-briefs">
        {d.digests?.length ? <ul className="space-y-1.5">{d.digests.map((g) => (
          <li key={g.digest_id} className="text-sm"><span className="font-mono text-xs text-slate-500">{fmtTime(g.created_at)} · {g.trigger}</span> <span className="text-slate-900">{g.result?.headline ?? g.status}</span></li>
        ))}</ul> : <Empty>No briefs yet.</Empty>}
      </Section>
      <Section title="Ask Daybreyk questions" id="admin-ask">
        {d.ask_messages?.filter((m) => m.role === "user").length ? <ul className="space-y-1.5">{d.ask_messages.filter((m) => m.role === "user").map((m, i) => (
          <li key={i} className="text-sm text-slate-800"><MessageCircleQuestion className="inline w-3.5 h-3.5 text-slate-500 mr-1" />{m.text} <span className="font-mono text-[11px] text-slate-500">{fmtTime(m.ts)}</span></li>
        ))}</ul> : <Empty>No questions asked.</Empty>}
      </Section>
    </div>
  );
};

const StatsStrip = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data ?? null)).catch(() => setS(null)); }, []);
  if (!s) return null;
  const daily = Array.isArray(s.daily) ? s.daily : [];
  const max = Math.max(1, ...daily.map((d) => d.active ?? 0));
  const tiles = [["Sign-ups this week", s.signups_week, "admin-stat-signups"], ["Active today", s.active_today, "admin-stat-active-today"], ["Active this week", s.active_week, "admin-stat-active-week"],
    ["Avg daily users", s.avg_daily, "admin-stat-avg"], ["Total users", s.total_users, "admin-stat-total"], ["Briefs today", s.briefs_today, "admin-stat-briefs"]];
  return (
    <section data-testid="admin-stats" className="mt-6 rounded-3xl bg-navy-900 border border-navy-200 border-b-4 border-b-gold-400 p-5 sm:p-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map(([l, n, id]) => (
          <div key={id} data-testid={id} className="rounded-2xl bg-black/30 p-3"><p className="font-display text-3xl text-white leading-none">{n ?? 0}</p><p className="mt-1 text-xs font-semibold text-navy-800">{l}</p></div>
        ))}
      </div>
      <div data-testid="admin-daily-chart" className="mt-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-bold text-gold-300">Daily active users · last 7 days</p>
        <div className="mt-3 flex items-end gap-2 h-24">
          {daily.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-xs font-semibold text-white">{d.active}</span>
              <div className="w-full rounded-t-lg bg-gold-400" style={{ height: `${Math.max(4, (d.active / max) * 64)}px` }} />
              <span className="font-mono text-[10px] text-navy-800">{new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short" })}{d.signups ? ` +${d.signups}` : ""}</span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-navy-800">"+n" = new sign-ups that day. Daily activity counts anyone who opened Daybreyk.</p>
      </div>
    </section>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [params, setParams] = useSearchParams();
  const sel = params.get("u");
  const isAdmin = Boolean(user?.is_admin);
  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => setUsers([]));
  useEffect(() => { if (isAdmin) loadUsers(); }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (user && !isAdmin) return <Navigate to="/" replace />;

  return (
    <main data-testid="admin-page" className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-gold-300 flex items-center gap-2"><Shield className="w-4 h-4" />Admin</p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl text-slate-900">All users</h1>
      <p data-testid="admin-user-count" className="mt-2 text-slate-600 flex items-center gap-2"><Users className="w-4 h-4" />{users ? `${users.length} account${users.length === 1 ? "" : "s"}` : "Loading…"} · <Link to="/" className="text-brand-dark font-semibold">Back to my brief</Link></p>
      {isAdmin && <StatsStrip />}
      {isAdmin && <div className="mt-6 space-y-4"><SafetyPanel /><AnnouncementsAdmin /><ContactInbox /><UsageTrends /></div>}
      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,380px)_1fr]">
        <div className={`space-y-2.5 ${sel ? "hidden lg:block" : ""}`}>
          {(users ?? []).map((u) => <UserRow key={u.user_id} u={u} active={u.user_id === sel} onPick={(id) => setParams({ u: id })} />)}
        </div>
        <div className={sel ? "" : "hidden lg:block"}>
          {sel ? <Detail uid={sel} onBack={() => setParams({})} onChanged={loadUsers} /> : <p className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-500">Pick a user to see their data.</p>}
        </div>
      </div>
    </main>
  );
}
